import { UntypedFormGroup } from '@angular/forms';
import { BadgeColor } from '@vendasta/galaxy/badge';
import { FulfillmentFormConfigInterface, FulfillmentOrderStatus } from '@vendasta/order-fulfillment';
import { buildTemporaryStorageKey } from '@vendasta/store';
import { WorkOrderPersona } from './work-order-landing-page/interfaces';

const vendorStatusColorMap = new Map<FulfillmentOrderStatus, BadgeColor | undefined>([
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED, 'purple'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW, 'yellow'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_PROGRESS, 'blue'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_COMPLETED, 'green'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_CANCELLED, 'red'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_UNSET, undefined],
]);

const nonVendorStatusColorMap = new Map<FulfillmentOrderStatus, BadgeColor | undefined>([
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED, 'yellow'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW, 'purple'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_PROGRESS, 'blue'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_COMPLETED, 'green'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_CANCELLED, 'red'],
  [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_UNSET, undefined],
]);

export const statusColorMap = new Map<WorkOrderPersona, Map<FulfillmentOrderStatus, BadgeColor | undefined>>([
  [WorkOrderPersona.VENDOR, vendorStatusColorMap],
  [WorkOrderPersona.CP, nonVendorStatusColorMap],
  [WorkOrderPersona.SALES, nonVendorStatusColorMap],
  [WorkOrderPersona.SMB, nonVendorStatusColorMap],
]);

export enum DisplayedStatusValuesEnum {
  UNSET = '',
  DETAILS_NEEDED = 'DISPLAYED_STATUS_VALUES.DETAILS_NEEDED',
  IN_REVIEW = 'DISPLAYED_STATUS_VALUES.IN_REVIEW',
  VENDOR_REVIEW = 'DISPLAYED_STATUS_VALUES.VENDOR_REVIEW',
  IN_PROGRESS = 'DISPLAYED_STATUS_VALUES.IN_PROGRESS',
  COMPLETED = 'DISPLAYED_STATUS_VALUES.COMPLETED',
  CANCELED = 'DISPLAYED_STATUS_VALUES.CANCELED',
  ON_HOLD = 'DISPLAYED_STATUS_VALUES.ON_HOLD',
}

// Persona's that should see "Vendor review" as the in review status
const VENDOR_REVIEW_PERSONAS = [WorkOrderPersona.CP, WorkOrderPersona.SALES];

export const fulfillmentOrderStatusToString = (
  status: FulfillmentOrderStatus,
  persona?: WorkOrderPersona,
): DisplayedStatusValuesEnum => {
  switch (status) {
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED:
      return DisplayedStatusValuesEnum.DETAILS_NEEDED;
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_PROGRESS:
      return DisplayedStatusValuesEnum.IN_PROGRESS;
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_COMPLETED:
      return DisplayedStatusValuesEnum.COMPLETED;
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_CANCELLED:
      return DisplayedStatusValuesEnum.CANCELED;
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_ON_HOLD:
      return DisplayedStatusValuesEnum.ON_HOLD;
    case FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW:
      return VENDOR_REVIEW_PERSONAS.includes(persona)
        ? DisplayedStatusValuesEnum.VENDOR_REVIEW
        : DisplayedStatusValuesEnum.IN_REVIEW;
    default:
      return DisplayedStatusValuesEnum.UNSET;
  }
};

export const displayedStatusToFulfillmentOrderStatus = (
  displayStatus: DisplayedStatusValuesEnum,
): FulfillmentOrderStatus => {
  switch (displayStatus) {
    case DisplayedStatusValuesEnum.DETAILS_NEEDED:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED;
    case DisplayedStatusValuesEnum.IN_REVIEW:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW;
    case DisplayedStatusValuesEnum.VENDOR_REVIEW:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_REVIEW;
    case DisplayedStatusValuesEnum.IN_PROGRESS:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_IN_PROGRESS;
    case DisplayedStatusValuesEnum.COMPLETED:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_COMPLETED;
    case DisplayedStatusValuesEnum.CANCELED:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_CANCELLED;
    case DisplayedStatusValuesEnum.ON_HOLD:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_ON_HOLD;
    default:
      return FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_UNSET;
  }
};

export function hasFulfillmentForm(formConfig: FulfillmentFormConfigInterface): boolean {
  return formConfig?.orderForm?.length > 0;
}
export function getTemporaryStorageKey(businessId: string, appId: string): string {
  return buildTemporaryStorageKey({
    businessId: businessId,
    id: appId,
    index: appId.startsWith('A-') ? 0 : undefined,
  });
}

/**
 * check to see if changes for work order form are saved in session storage
 * @param formGroup - form group to check
 * @returns boolean
 */
export function doesWorkOrderFormHaveUnsavedChanges(formGroup: UntypedFormGroup): boolean {
  const keysToCheck: string[] = Object.keys(formGroup.controls || {});
  return keysToCheck?.reduce((prev, currentKey) => {
    const cachedFormValue = sessionStorage.getItem(currentKey);
    if (cachedFormValue) {
      return true;
    }
    return prev;
  }, false);
}

/**
 * clear unsaved changes for a form group from session storage
 * @param form
 */
export function clearUnsavedChangesFromSessionStorage(formGroup: UntypedFormGroup): void {
  const keysToCheck: string[] = Object.keys(formGroup.controls || {});
  keysToCheck.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}
